import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '@/api/useAccount';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import {
  AvailabilityNotPermittedError,
  useDeleteAvailability,
  useDeleteTimeOff,
  useTechnicianAvailability,
  useTechnicianTimeOff,
  type TechnicianAvailability,
  type TechnicianTimeOff,
} from '@/routes/technicians/useAvailability';
import { AvailabilityDialog, TimeOffDialog } from './TechnicianAvailabilityDialogs';

/**
 * A person's working hours and time off, on their profile page (M1-39).
 *
 * <b>Keyed on the USER id</b>, like `TechnicianSkillSection` (M1-38) and unlike
 * `TechnicianLicenseSection` (M1-37) — so, like the skills section, it renders
 * on the "no profile yet" branch of `TechnicianProfileDetail` too. That is not
 * a nicety here: the real dev account has three technicians and ZERO technician
 * profiles, so a profile-keyed section would render for nobody at all.
 *
 * <b>Read-only unless the viewer holds `users.manage`</b>, including for the
 * technician looking at their OWN availability — the same call M1-37 and M1-38
 * both made, and the AC states it outright ("technicians can view their own
 * availability (read-only, same as licenses)"). So there is no add, edit or
 * remove affordance for them at all, rather than controls that render and then
 * 403. The API is still the enforcement point; this only decides what to draw.
 */
export function TechnicianAvailabilitySection({
  userId,
  canManage,
  onAnnounce,
}: {
  userId: string;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  // ⚠️ Only for the dialogs' date defaults, and that is not a nicety: those
  // defaults are stored and then compared against the account-local date, so
  // seeding them from the VIEWER's clock quietly wrote the wrong day for
  // anybody working from a different zone than the shop. See `todayIso` in
  // TechnicianAvailabilityDialogs. The query is the app-wide cached one, so
  // this costs no request of its own.
  const timeZone = useAccount().data?.timezone;
  const template = useTechnicianAvailability(userId);
  const timeOff = useTechnicianTimeOff(userId);
  const removeDay = useDeleteAvailability(userId);
  const removeTimeOff = useDeleteTimeOff(userId);

  const [editingDay, setEditingDay] = useState<TechnicianAvailability | null>(null);
  const [addingDay, setAddingDay] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState<TechnicianTimeOff | null>(null);
  const [addingTimeOff, setAddingTimeOff] = useState(false);
  const [pendingRemoveDay, setPendingRemoveDay] = useState<TechnicianAvailability | null>(null);
  const [pendingRemoveTimeOff, setPendingRemoveTimeOff] = useState<TechnicianTimeOff | null>(null);

  // A technician viewing someone else's profile: the API refuses, and that is
  // a real state rather than a load failure worth a retry button.
  if (
    template.error instanceof AvailabilityNotPermittedError ||
    timeOff.error instanceof AvailabilityNotPermittedError
  ) {
    return null;
  }

  return (
    <div className="rounded-lg border p-4" data-testid="availability-section">
      <h2 className="text-base font-semibold">{t('technicianAvailability.heading')}</h2>

      {/* --- Weekly template ------------------------------------------- */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{t('technicianAvailability.templateHeading')}</h3>
        {canManage ? (
          <Button
            variant="outline"
            onClick={() => {
              setAddingDay(true);
            }}
          >
            {t('technicianAvailability.addDay.action')}
          </Button>
        ) : null}
      </div>

      {template.isPending ? (
        <LoadingState label={t('technicianAvailability.loading')} />
      ) : template.isError ? (
        <ErrorState
          title={t('technicianAvailability.loadErrorTitle')}
          description={t('technicianAvailability.loadErrorDescription')}
          onRetry={() => void template.refetch()}
        />
      ) : template.data.length === 0 ? (
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          {canManage
            ? t('technicianAvailability.emptyCanManage')
            : t('technicianAvailability.emptyReadOnly')}
        </p>
      ) : (
        <ul className="mt-2 grid gap-2" data-testid="availability-days">
          {template.data.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <span className="text-sm">
                <span className="font-medium">{t(`technicianAvailability.day.${row.day}`)}</span>{' '}
                {row.startTime}–{row.endTime}
                <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                  {row.effectiveTo
                    ? t('technicianAvailability.effectiveRange', {
                        from: row.effectiveFrom,
                        to: row.effectiveTo,
                      })
                    : t('technicianAvailability.effectiveFrom', { date: row.effectiveFrom })}
                </span>
              </span>
              {canManage ? (
                <span className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingDay(row)}>
                    {t('technicianAvailability.editAction')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // reset() first: this mutation is hoisted to the section
                      // and outlives each ConfirmDialog, so without it a failed
                      // removal leaves isError true and the NEXT row's dialog
                      // opens already announcing a failure nobody caused. The
                      // same trap TechnicianLicenseSection documents.
                      removeDay.reset();
                      setPendingRemoveDay(row);
                    }}
                  >
                    {t('technicianAvailability.removeAction')}
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* --- Time off ---------------------------------------------------- */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{t('technicianAvailability.timeOffHeading')}</h3>
        {canManage ? (
          <Button variant="outline" onClick={() => setAddingTimeOff(true)}>
            {t('technicianAvailability.addTimeOff.action')}
          </Button>
        ) : null}
      </div>

      {timeOff.isPending ? (
        <LoadingState label={t('technicianAvailability.loading')} />
      ) : timeOff.isError ? (
        <ErrorState
          title={t('technicianAvailability.loadErrorTitle')}
          description={t('technicianAvailability.loadErrorDescription')}
          onRetry={() => void timeOff.refetch()}
        />
      ) : timeOff.data.length === 0 ? (
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          {canManage
            ? t('technicianAvailability.timeOffEmptyCanManage')
            : t('technicianAvailability.timeOffEmptyReadOnly')}
        </p>
      ) : (
        <ul className="mt-2 grid gap-2" data-testid="time-off-entries">
          {timeOff.data.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <span className="text-sm">
                <span className="font-medium">{t(`technicianAvailability.kind.${row.kind}`)}</span>{' '}
                {t('technicianAvailability.effectiveRange', {
                  from: row.startDate,
                  to: row.endDate,
                })}
                {row.notes ? (
                  <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                    {row.notes}
                  </span>
                ) : null}
              </span>
              {canManage ? (
                <span className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingTimeOff(row)}>
                    {t('technicianAvailability.editAction')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      removeTimeOff.reset();
                      setPendingRemoveTimeOff(row);
                    }}
                  >
                    {t('technicianAvailability.removeAction')}
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {addingDay || editingDay ? (
        <AvailabilityDialog
          userId={userId}
          timeZone={timeZone}
          row={editingDay ?? undefined}
          onClose={() => {
            setAddingDay(false);
            setEditingDay(null);
          }}
          onSaved={(message) => {
            setAddingDay(false);
            setEditingDay(null);
            onAnnounce(message);
          }}
        />
      ) : null}

      {addingTimeOff || editingTimeOff ? (
        <TimeOffDialog
          userId={userId}
          timeZone={timeZone}
          row={editingTimeOff ?? undefined}
          onClose={() => {
            setAddingTimeOff(false);
            setEditingTimeOff(null);
          }}
          onSaved={(message) => {
            setAddingTimeOff(false);
            setEditingTimeOff(null);
            onAnnounce(message);
          }}
        />
      ) : null}

      {pendingRemoveDay ? (
        <ConfirmDialog
          titleId="remove-availability-title"
          title={t('technicianAvailability.remove.title')}
          description={t('technicianAvailability.remove.body')}
          confirmLabel={t('technicianAvailability.remove.confirm')}
          isPending={removeDay.isPending}
          errorMessage={removeDay.isError ? t('technicianAvailability.remove.failed') : undefined}
          onConfirm={() =>
            removeDay.mutate(pendingRemoveDay.id, {
              onSuccess: () => {
                setPendingRemoveDay(null);
                onAnnounce(t('technicianAvailability.remove.done'));
              },
            })
          }
          onClose={() => setPendingRemoveDay(null)}
        />
      ) : null}

      {pendingRemoveTimeOff ? (
        <ConfirmDialog
          titleId="remove-time-off-title"
          title={t('technicianAvailability.remove.title')}
          description={t('technicianAvailability.remove.body')}
          confirmLabel={t('technicianAvailability.remove.confirm')}
          isPending={removeTimeOff.isPending}
          errorMessage={
            removeTimeOff.isError ? t('technicianAvailability.remove.failed') : undefined
          }
          onConfirm={() =>
            removeTimeOff.mutate(pendingRemoveTimeOff.id, {
              onSuccess: () => {
                setPendingRemoveTimeOff(null);
                onAnnounce(t('technicianAvailability.remove.done'));
              },
            })
          }
          onClose={() => setPendingRemoveTimeOff(null)}
        />
      ) : null}
    </div>
  );
}
